import { Button, FormControl, FormHelperText, FormLabel, Input, Stack } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link as RouterLink, useNavigate, useParams } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';

import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ProcessMap } from '@/components/processes/map/ProcessMap';
import { SelectedProcessSigns } from '@/components/processes/map/SelectedProcessSigns';
import { I18N_DE } from '@/i18n/de';
import { CreateProcessDto } from '@/modules/api';
import { backendApiProcessesCreateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useProcessMapStore } from '@/stores/useProcessMapStore';
import { ProcessType } from '@/types/ProcessType';

function getProcessType(external: boolean | undefined): ProcessType | undefined {
  if (external === undefined) return undefined;

  return external ? ProcessType.EXTERNAL : ProcessType.NORMAL;
}

function RouteComponent() {
  const { adminLevelId } = useParams({ strict: false });
  const signs = useProcessMapStore((state) => state.signs);
  const { reset } = useProcessMapStore((state) => state.actions);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<Pick<CreateProcessDto, 'external' | 'sequentialId'>>();

  const isExternal = watch('external');
  const sequentialId = watch('sequentialId');

  const {
    mutate,
    error,
    isPending,
    reset: resetMutation,
  } = useMutation({
    ...backendApiProcessesCreateMutation(),
    onSuccess: (process) => {
      reset();
      resetMutation();
      navigate({
        to: '/vorgaenge/$adminLevelId/$processId/edit',
        params: {
          adminLevelId: adminLevelId || 'recherche',
          processId: process.id,
        },
      });
    },
  });

  return (
    <Stack direction="row" gap={2} flexGrow={1}>
      <ProcessMap />
      <Stack
        flex={1}
        gap={2}
        maxWidth={300}
        component="form"
        onSubmit={handleSubmit((data) => {
          if (isPending) return;

          mutate({ body: { signIds: signs.map(({ id }) => id), ...data } });
        })}
      >
        <Stack direction="row" gap={2}>
          <Button
            disabled={signs.length === 0 || isExternal === undefined || isPending || (isExternal && !sequentialId)}
            loading={isPending}
            type="submit"
            sx={{ flexGrow: 1 }}
          >
            Vorgang erstellen
          </Button>
          <Button variant="plain" component={RouterLink} to={`/vorgaenge/${adminLevelId}`}>
            Zurück
          </Button>
        </Stack>
        <FormControl error={!!errors.external}>
          <FormLabel>Vorgangstyp</FormLabel>
          <Controller
            control={control}
            name="external"
            rules={{ validate: (value) => value !== undefined || 'Pflichtfeld' }}
            render={({ field }) => (
              <RadioWithDescriptionGroup
                name={field.name}
                onBlur={field.onBlur}
                value={getProcessType(field.value)}
                onChange={(processType) => field.onChange(processType === ProcessType.EXTERNAL)}
                options={Object.values(ProcessType)}
                optionLabel={getI18nRadioLabel({
                  i18nTitle: I18N_DE.ProcessType,
                  i18nDescription: I18N_DE.ProcessTypeDescription,
                })}
              />
            )}
          />
          {errors.external && <FormHelperText>{errors.external.message}</FormHelperText>}
        </FormControl>
        {isExternal && (
          <FormControl>
            <FormLabel>Vorgangsnummer angeben</FormLabel>
            <Input variant="outlined" {...register('sequentialId', { required: true, minLength: 2 })} />
            {errors.sequentialId && <FormHelperText>Pflichtfeld</FormHelperText>}
          </FormControl>
        )}

        {error && <ErrorAlert error={error} onClose={resetMutation} />}

        <SelectedProcessSigns />
      </Stack>
    </Stack>
  );
}

export const Route = createFileRoute('/_authenticated/_management/vorgaenge/$adminLevelId/karte/')({
  component: RouteComponent,
});
